import { useTranslation } from 'react-i18next';
import type { Guidance } from '@fla/core';

function Chevron({ dir, className }: { dir: 'left' | 'right'; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-16 w-16 ${className ?? ''}`}
      fill="currentColor"
      aria-hidden
    >
      {dir === 'left' ? (
        <path d="M15 4l-8 8 8 8 2.2-2.1L11.4 12l5.8-5.9z" />
      ) : (
        <path d="M9 4l8 8-8 8-2.2-2.1L12.6 12 6.8 6.1z" />
      )}
    </svg>
  );
}

/** Big signed-deviation readout: number + steer arrow (or "on line"). */
export function SteerArrow({ g }: { g: Guidance }) {
  const { t } = useTranslation();
  const cm = Math.round(Math.abs(g.crossTrackM) * 100);
  const on = g.steer === 'on';
  const color = on ? 'text-emerald-400' : g.steer === 'left' ? 'text-amber-400' : 'text-sky-400';

  if (on) {
    return (
      <div className="text-center text-4xl font-bold text-emerald-400">{t('driving.onLine')}</div>
    );
  }
  return (
    <div className="flex items-center justify-center gap-5">
      {g.steer === 'left' && <Chevron dir="left" className={color} />}
      <div className="text-center leading-none">
        <div className={`text-7xl font-black tabular-nums ${color}`}>{cm}</div>
        <div className="mt-1 text-lg text-white/70">
          cm · {t(g.steer === 'left' ? 'driving.steerLeft' : 'driving.steerRight')}
        </div>
      </div>
      {g.steer === 'right' && <Chevron dir="right" className={color} />}
    </div>
  );
}

/** Classic ag lightbar: segments light from centre toward the side to steer. */
export function Lightbar({ g }: { g: Guidance }) {
  const N = 11;
  const center = 5;
  const segCm = 5;
  const cm = Math.abs(g.crossTrackM) * 100;
  const lit = Math.min(center, Math.ceil(cm / segCm));
  const on = g.steer === 'on';

  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: N }).map((_, i) => {
        const off = i - center;
        const isCenter = i === center;
        let cls = 'bg-white/10';
        let glow = false;
        if (on && isCenter) {
          cls = 'bg-emerald-400';
          glow = true;
        } else if (!on && g.steer === 'left' && off < 0 && off >= -lit) {
          cls = 'bg-amber-400';
          glow = true;
        } else if (!on && g.steer === 'right' && off > 0 && off <= lit) {
          cls = 'bg-sky-400';
          glow = true;
        }
        return (
          <div
            key={i}
            className={`h-12 rounded ${isCenter ? 'w-3' : 'w-6'} ${cls} ${glow ? 'shadow-[0_0_14px_currentColor]' : ''}`}
          />
        );
      })}
    </div>
  );
}
